import {
  useGetProgramDraftWorkoutsQuery,
  useGetProgramArchivedWorkoutsQuery,
  useGetProgramScheduledWorkoutsQuery,
  useGetProgramByIdQuery,
} from '../../../../../generated/graphql';
import { resultsToAsync } from '../../../helpers/gql';
import {
  DayName,
  FixedTitle,
  LiteWorkout,
  ProgramWithWorkouts,
  RawWorkout,
  WeekName,
  WorkoutType,
  WorkoutsStructure,
} from './workouts.domain';
import { Async } from '../../../helpers/types';
import { Program } from '@playbook-technologies/common-enums';
import { useState } from 'react';
import { makeAsync } from '../../../helpers/async';
import auth from '@client/components/auth';

const toDayName = (v: string) => {
  const match = v.match(/^[Ww]\s*\d+\s*\/\s*[dD]\s*(\d+)/);
  if (!match) return { orgDay: undefined, day: undefined };
  const rawDay = parseInt(match[1], 10);
  const day = rawDay <= 0 || rawDay > 7 ? 1 : rawDay;
  return { orgDay: `D${rawDay}` as DayName, day: `D${day}` as DayName };
};

const toWeekName = (v: string) => {
  const match = v.match(/^[Ww]\s*(\d+)/);
  if (!match) return { orgWeek: undefined, week: undefined };
  const rawWeek = parseInt(match[1], 10);
  const week = rawWeek <= 0 || rawWeek > 20 ? 1 : rawWeek;
  return { orgWeek: `W${rawWeek}` as WeekName, week: `W${week}` as WeekName };
};

const toFixedTitle = (v: string) => {
  const match = v.match(/^[Ww]\s*\d+\s*\/\s*[Dd]\s*\d+\s*(.*)/);
  return (match ? match[1] : v) as FixedTitle;
};

export const toTitle = (
  w: LiteWorkout,
  delta: Pick<LiteWorkout, 'day' | 'week'>,
) => {
  const { day = w.orgDay, week = w.orgWeek } = delta;
  return `${week}/${day} ${w.fixedTitle}`;
};

export const toLiteWorkout = (
  v: RawWorkout,
  type: WorkoutType,
): LiteWorkout => {
  const title = v.name || '';

  return {
    workoutId: v.id,
    fixedTitle: toFixedTitle(title),
    orgTitle: title,
    type,
    scheduledDate: type === 'scheduled' ? v.publishedAt || '' : '',
    programId: v.programId || null,
    publishedDate: v.publishedAt || '',
    archivedDate: v.archivedAt,
    lastEditedDate: v.updatedAt,
    rating: v.statsRatingAverage || 0,
    begins: v.statsBegins || 0,
    completions: v.statsCompletions || 0,
    comments: v.statsComments || 0,
    ...toDayName(title),
    ...toWeekName(title),
  };
};

const sortByArchivedDate = (a: LiteWorkout, b: LiteWorkout) => {
  if (!a.archivedDate || !b.archivedDate) return 0;
  return Date.parse(b.archivedDate) - Date.parse(a.archivedDate);
};

export const useProgramWithWorkouts = ({
  creatorId,
  programId,
  selectedList,
}: {
  creatorId: number;
  programId: number;
  selectedList: WorkoutType;
}): Async<ProgramWithWorkouts> => {
  const baseSkip = creatorId === null;

  const programParams = {
    variables: { id: programId },
    skip: baseSkip,
    fetchPolicy: 'network-only' as const,
  };

  const bucketParams = (type: WorkoutType) => ({
    variables: { programId },
    skip: baseSkip || selectedList !== type,
    fetchPolicy: 'cache-and-network' as const,
  });

  const draftsQuery = useGetProgramDraftWorkoutsQuery(bucketParams('draft'));
  const archivedQuery = useGetProgramArchivedWorkoutsQuery(
    bucketParams('archived'),
  );
  const scheduledQuery = useGetProgramScheduledWorkoutsQuery(
    bucketParams('scheduled'),
  );
  const programQuery = useGetProgramByIdQuery(programParams);
  const [refetching, setRefetching] = useState<Async<null>>(
    makeAsync.notFetched,
  );

  const activeBucketQuery =
    selectedList === 'draft'
      ? draftsQuery
      : selectedList === 'archived'
      ? archivedQuery
      : selectedList === 'scheduled'
      ? scheduledQuery
      : null;

  const refetch = () => {
    setRefetching(makeAsync.fetching());

    return Promise.all([
      programQuery.refetch(programParams.variables),
      activeBucketQuery ? activeBucketQuery.refetch({ programId }) : null,
    ])
      .then(() => setRefetching(makeAsync.fetched(null)))
      .catch((err) => setRefetching(makeAsync.error(err)));
  };

  const mapped = resultsToAsync([programQuery]);

  if (mapped.type !== 'fetched') return mapped;

  // setRefetching(makeAsync.fetched({}));
  const workoutsStructure: WorkoutsStructure =
    programQuery.data?.program.contentStructure ===
    Program.ContentStructure.Dayweek.value
      ? 'week/day'
      : 'series';

  const remap = (type: WorkoutType, ws: (RawWorkout | null)[] | null = []) =>
    (ws || [])
      .filter((w) => w !== null)
      .map((v) => toLiteWorkout(v as RawWorkout, type));

  const workouts: ProgramWithWorkouts['workouts'] = {
    archived: remap(
      'archived',
      archivedQuery.data?.program?.archivedWorkouts,
    ).sort(sortByArchivedDate),
    draft: remap('draft', draftsQuery.data?.program?.draftWorkouts),
    scheduled: remap(
      'scheduled',
      scheduledQuery.data?.program?.scheduledWorkouts || [],
    ),
    published: remap(
      'published',
      programQuery.data?.program.workouts?.filter((w) => w?.isVisible),
    ),
  };

  const isAdminOrModerator = auth.isAdministrator() || auth.isModerator();
  const isProgramEditable =
    isAdminOrModerator || !programQuery.data?.program.isLocked;

  return {
    type: 'fetched' as const,
    value: {
      workouts,
      workoutsStructure,
      isProgramEditable,
      programName: programQuery.data?.program.name ?? '',
      refetchingState: refetching.type,
      isWorkoutArchiveEnabled:
        programQuery.data?.program.accessType === 'premium',
      isSelectedListLoading: activeBucketQuery?.loading ?? false,
      refetch,
    },
  };
};
